import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Chip,
} from "@mui/material";
import { Close, CloudUpload } from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import domtoimage from "dom-to-image";
import { toast } from "react-toastify";
import TemplateRenderer from "./thumbnail-templates/TemplatesRenderer";
import type {
  IThumbnailAssignment,
  IThumbnailPreviewDialogProps,
} from "../types/thumbnail";
import type { IThumbnailTemplate } from "../../../../types/thumbnail-template";
import apiService from "../../../../service/Api";

const waitForExportImages = async (node: HTMLElement) => {
  const images = Array.from(node.querySelectorAll("img"));

  await Promise.all(
    images.map(
      (img) =>
        new Promise<void>((resolve) => {
          if (img.complete) {
            resolve();
            return;
          }

          const done = () => resolve();
          img.addEventListener("load", done, { once: true });
          img.addEventListener("error", done, { once: true });
        }),
    ),
  );

  await new Promise((resolve) => requestAnimationFrame(() => resolve(null)));
};

const ThumbnailPreviewDialog: React.FC<IThumbnailPreviewDialogProps> = ({
  open,
  onClose,
  streamData,
  onSave,
  onDownload,
  refreshThumbnails,
  thumbnailType,
  refreshStream,
}) => {
  const [selectedTemplate, setSelectedTemplate] =
    useState<IThumbnailTemplate | null>(null);

  const [isSaving, setIsSaving] = useState(false);
  const [showAllTemplates, setShowAllTemplates] = useState(false);

  const [customBackground, setCustomBackground] = useState<string>("");
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  // UI preview ref (optional)
  const previewRef = useRef<HTMLDivElement>(null);

  // Export ref (this is the one we download from)
  const exportRef = useRef<HTMLDivElement>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const bookingType = streamData?.bookingData?.booking_type;
  const deceasedId = streamData?.bookingData?.deceased_ids || [];
  const streamId = streamData?.id;

  const usesConferenceTemplate =
    bookingType === "CONFERENCE" || bookingType === "CHURCH";

  const categoryMap: Record<string, IThumbnailTemplate["category"]> = {
    FUNERAL: "funeral",
    CHURCH: "church",
    FUNERAL_HOME: "funeral_home",
    CONFERENCE: "conference",
  };

  const [templates, setTemplates] = useState<IThumbnailTemplate[]>([]);
  const [, setLoadingTemplates] = useState<boolean>(true);

  const effectiveCategory: IThumbnailTemplate["category"] | undefined =
    bookingType === "CHURCH"
      ? "conference"
      : bookingType
        ? categoryMap[bookingType as keyof typeof categoryMap]
        : undefined;

  const displayTemplates = templates.filter((template) => {
    const isActive = template.is_active;

    const matchesThumbnailType =
      !thumbnailType || template.thumbnail_type === thumbnailType;

    const matchesBookingType =
      !effectiveCategory || template.category === effectiveCategory;

    return isActive && matchesThumbnailType && matchesBookingType;
  });

  const fetchTemplates = useCallback(async () => {
    try {
      const params = new URLSearchParams();

      if (thumbnailType) {
        params.append("thumbnail_type", thumbnailType);
      }

      if (effectiveCategory) {
        params.append("category", effectiveCategory);
      }

      const response = await apiService.get<{
        results: IThumbnailTemplate[];
      }>(`/api/configurations/thumbnails/?${params.toString()}`);

      setTemplates(response.data.results || []);
    } catch (error) {
      console.error("Error fetching templates:", error);
      setTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  }, [thumbnailType, effectiveCategory]);

  useEffect(() => {
    if (open) fetchTemplates();
  }, [fetchTemplates, open]);

  useEffect(() => {
    setSelectedTemplate(null);
    setShowAllTemplates(false);
    setCustomBackground("");
    setUploadedFile(null);
  }, [open, thumbnailType]);

  const handleBackgroundUpload = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      setCustomBackground(result);
      setUploadedFile(file);
      toast.success("Background image uploaded");
    };
    reader.readAsDataURL(file);
  };

  const { exportWidth, exportHeight } = useMemo(() => {
    if (!selectedTemplate) return { exportWidth: 1280, exportHeight: 720 };
    return selectedTemplate.thumbnail_type === "aone"
      ? { exportWidth: 1600, exportHeight: 1129 }
      : { exportWidth: 1280, exportHeight: 720 };
  }, [selectedTemplate]);

  // Shared by handleDownload (gives the user a copy) and handleSave (persists
  // it server-side) -- both need the exact same composited export: the
  // template's background PLUS all the text it overlays on top, not just
  // the raw uploaded background (which is all stream_thumbnail_background_image
  // holds, and that field also doubles as the memorial page's hero banner
  // image -- so it can't be repurposed to hold this instead).
  const exportComposedThumbnailBlob = useCallback(async (): Promise<Blob | null> => {
    if (!exportRef.current) return null;
    await waitForExportImages(exportRef.current);
    return domtoimage.toBlob(exportRef.current, {
      width: exportWidth,
      height: exportHeight,
      bgcolor: "#000",
      cacheBust: true,
    });
  }, [exportWidth, exportHeight]);

  const handleDownload = useCallback(async () => {
    try {
      if (!selectedTemplate) {
        toast.error("Please select a template first");
        return;
      }

      if (!exportRef.current) {
        console.log("exportRef.current is null", exportRef.current);
        toast.error("Thumbnail not ready yet. Try again in a second.");
        return;
      }

      await waitForExportImages(exportRef.current);

      const dataUrl = await domtoimage.toPng(exportRef.current, {
        width: exportWidth,
        height: exportHeight,
        bgcolor: "#000",
        cacheBust: true,
      });

      const fileName = `thumbnail-${
        streamData?.bookingData?.deceased_name || streamData?.title || "event"
      }.png`;

      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = fileName;
      link.click();

      onDownload?.();
    } catch (err) {
      console.error("Thumbnail download failed:", err);
      toast.error("Download failed. Check console for details.");
    }
  }, [exportHeight, exportWidth, onDownload, selectedTemplate, streamData]);

  const handleSave = async () => {
    if (!selectedTemplate || !streamId) return;

    try {
      setIsSaving(true);

      const payload: {
        deceased_ids: number[];
        stream_id: number;
        thumbnail_id: number;
      } = {
        deceased_ids: deceasedId,
        stream_id: Number(streamId),
        thumbnail_id: Number(selectedTemplate.id),
      };

      const { data } = await apiService.get<{
        results: IThumbnailAssignment[];
      }>(`/api/configurations/deceased-thumbnails/?stream=${streamId}`);

      const existing = data.results[0];
      if (existing) {
        await apiService.delete(
          `/api/configurations/deceased-thumbnails/${existing.id}/`,
        );
      }

      await apiService.post(
        "/api/configurations/deceased-thumbnails/",
        payload,
      );

      // Upload conference background if provided
      if (usesConferenceTemplate && uploadedFile) {
        const formData = new FormData();
        formData.append("stream_thumbnail_background_image", uploadedFile);

        try {
          await apiService.patch(`/api/streams/stream/${streamId}/`, formData);
          toast.success("Custom background saved to stream");
        } catch (patchError) {
          console.error("Stream background patch failed:", patchError);
          toast.error("Failed to upload stream background");
        }
      }

      // Persist the finished composited thumbnail (background + overlaid
      // text) so it can be reused elsewhere (e.g. pre-filling the YouTube
      // thumbnail upload) instead of requiring a manual download+reupload.
      // Non-fatal on failure -- the template assignment above already saved.
      try {
        const composedBlob = await exportComposedThumbnailBlob();
        if (composedBlob) {
          const thumbFormData = new FormData();
          thumbFormData.append("thumbnail", composedBlob, "thumbnail.png");
          await apiService.post(
            `/api/streams/stream/${streamId}/save-generated-thumbnail/`,
            thumbFormData,
          );
          if (refreshStream) await refreshStream();
        }
      } catch (exportError) {
        console.error("Failed to save generated thumbnail:", exportError);
      }

      toast.success("Thumbnail saved successfully");

      onSave?.("thumbnail-saved");
      refreshThumbnails?.();
      if (refreshStream) await refreshStream();
      onClose();
    } catch (error) {
      console.error("Thumbnail save error", error);
      toast.error("Failed to save thumbnail");
    } finally {
      setIsSaving(false);
    }
  };

  const handleChooseDifferent = () => setShowAllTemplates(true);

  const getCategoryColor = (category: IThumbnailTemplate["category"]) => {
    switch (category) {
      case "funeral":
        return "primary";
      case "church":
        return "secondary";
      case "funeral_home":
        return "success";
      case "conference":
        return "info";
      default:
        return "default";
    }
  };

  const renderPreview = () => {
    if (!selectedTemplate) return null;

    return (
      <div
        ref={previewRef}
        style={{
          width: exportWidth,
          height: exportHeight,
          overflow: "hidden",
          background: "#000",
        }}
      >
        <TemplateRenderer
          streamData={streamData}
          template={selectedTemplate}
          customBackground={
            usesConferenceTemplate ? customBackground : undefined
          }
          style={{
            width: `${exportWidth}px`,
            height: `${exportHeight}px`,
          }}
        />
      </div>
    );
  };

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        accept="image/*"
        onChange={handleBackgroundUpload}
      />

      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography variant="h5">
            {showAllTemplates ? "Choose a Template" : "Thumbnail Preview"}
          </Typography>
          <Close onClick={onClose} style={{ cursor: "pointer" }} />
        </DialogTitle>

        <DialogContent sx={{ minHeight: "500px", py: 3 }}>
          {/* Hidden export node (full resolution, NOT scaled) */}
          {selectedTemplate && !showAllTemplates && (
            <Box
              sx={{
                position: "fixed",
                left: "-99999px",
                top: 0,
                width: exportWidth,
                height: exportHeight,
                overflow: "hidden",
                background: "#000",
              }}
            >
              <div
                ref={exportRef}
                style={{
                  width: exportWidth,
                  height: exportHeight,
                }}
              >
                <TemplateRenderer
                  streamData={streamData}
                  template={selectedTemplate}
                  customBackground={
                    usesConferenceTemplate ? customBackground : undefined
                  }
                  style={{
                    width: `${exportWidth}px`,
                    height: `${exportHeight}px`,
                  }}
                />
              </div>
            </Box>
          )}

          {selectedTemplate && !showAllTemplates ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap={2}
            >
              {/* Visible preview (scaled) */}
              <Box sx={{ transform: "scale(0.6)", transformOrigin: "center" }}>
                {renderPreview()}
              </Box>
            </Box>
          ) : (
            <Grid container spacing={2}>
              <AnimatePresence>
                {displayTemplates.map((template, index) => (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={template.id}>
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                    >
                      <Card
                        onClick={() => {
                          setSelectedTemplate(template);
                          setShowAllTemplates(false);
                        }}
                        sx={{
                          cursor: "pointer",
                          border: "1px solid #ccc",
                          "&:hover": { boxShadow: 6 },
                        }}
                      >
                        <CardMedia
                          component="img"
                          height="150"
                          image={
                            template.foreground_image ||
                            template.background_image
                          }
                          alt={template.name}
                        />
                        <CardContent>
                          <Typography variant="h6">{template.name}</Typography>
                          <Chip
                            label={template.category.replace("_", " ")}
                            color={getCategoryColor(template.category)}
                          />
                        </CardContent>
                      </Card>
                    </motion.div>
                  </Grid>
                ))}
              </AnimatePresence>
            </Grid>
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: "space-between", p: 2 }}>
          <Box>
            {!showAllTemplates &&
              selectedTemplate &&
              !usesConferenceTemplate && (
                <Button onClick={handleChooseDifferent}>
                  Choose Different Template
                </Button>
              )}
          </Box>

          <Box display="flex" gap={2}>
            {selectedTemplate && !showAllTemplates && (
              <>
                {usesConferenceTemplate && (
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <Button
                      variant="outlined"
                      startIcon={<CloudUpload />}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {customBackground
                        ? "Change Background Image"
                        : "Upload Background Image"}
                    </Button>
                  </Box>
                )}

                <Button onClick={handleDownload}>Download</Button>

                <Button
                  onClick={handleSave}
                  variant="contained"
                  disabled={isSaving}
                >
                  {isSaving ? <CircularProgress size={20} /> : "Save"}
                </Button>
              </>
            )}
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ThumbnailPreviewDialog;
